<template>
  <div>
    <div class="row m-4 mt-0 mb-0">
      <div class="col-sm-12">
        <div class="card card-body mb-0">
          <div class="row">
            <div class="form-group me-2 col-md-3 col-lg-3 col-xl-2">
              <div class="col p-0">
                <input type="text" v-model="search" class="form-control form-control-sm"
                       :placeholder="helper.trans('currencies.search_placeholder')" @keyup.enter="applyFilters()">
              </div>
            </div>
            <div class="form-group col">
              <div class="col-auto p-0 text-end">
                <button type="button" @click.prevent="applyFilters()" class="btn btn-outline-primary btn-sm rounded-pill me-2 text-capitalize-first">{{ helper.trans('button.apply') }}</button>
                <button type="button" @click.prevent="clearFilters()" class="btn btn-outline-secondary btn-sm rounded-pill text-capitalize-first">{{ helper.trans('button.reset') }}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <LoaderWrapper ref="pageLoader" class="h-100">
      <div class="row m-4 mt-0">
        <div class="col-sm-12">
          <div class="card card-body mb-0">
            <div class="row">
              <div class="col-sm-12">
                <h4 class="text-capitalize-first">{{ helper.trans('labels.currency_rates') }}</h4>
              </div>
            </div>

            <div class="row">
              <div class="col-sm-12">
                <div class="overflow-auto">
                  <table class="table table-striped border-light align-middle text-left text-nowrap position-relative">
                    <thead>
                    <tr>
                      <th class="text-capitalize-first">{{ helper.trans('labels.source') }}</th>
                      <th class="text-capitalize-first">{{ helper.trans('labels.from_currency') }}</th>
                      <th class="text-capitalize-first">{{ helper.trans('labels.to_currency') }}</th>
                      <th class="text-capitalize-first">{{ helper.trans('labels.date') }}</th>
                      <th class="text-capitalize-first">{{ helper.trans('labels.rate') }}</th>
                      <th class="text-capitalize-first">{{ helper.trans('labels.method') }}</th>
                      <th  class="text-end">
                        <button type="button" @click.prevent="showEditModal({})" class="btn btn-outline-primary btn-sm text-capitalize">{{ helper.trans('button.create') }}</button>

                        <b-modal dialog-class="modal-lg"
                                 :title="createModalTitle"
                                 :modelValue="createModal"
                                 body-class="pt-0"
                                 @update:modelValue="createModal = $event"
                        >
                          <template #default>
                            <CurrencyRateForm ref="createForm"></CurrencyRateForm>
                          </template>
                          <template #footer>
                            <button type="button" v-on:click="createModal = false" class="btn btn-outline-secondary btn-sm text-capitalize-first">{{ helper.trans('button.cancel') }}</button>
                            <LoaderWrapper ref="loaderCreateFrom" :zoom="0.5">
                              <button type="button" @click="save()" class="btn btn-outline-primary btn-sm text-capitalize-first">{{ helper.trans('button.save') }}</button>
                            </LoaderWrapper>
                        </template>
                      </b-modal>

                    </th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-if="response.items.length === 0">
                    <td colspan="7" class="text-center text-muted">{{ helper.trans('currencies.nothing_found') }}</td>
                  </tr>
                  <tr v-for="rate in response.items" :key="rate.id" class="">
                    <td>
                      <div v-if="rate.name">{{rate.name}} ({{rate.source}})</div>
                      <div v-else>{{rate.source}}</div>
                    </td>
                    <td>{{ rate.fromCurrency }}</td>
                    <td>{{ rate.toCurrency }}</td>
                    <td>{{ dateHelper.format(rate.date) }}</td>
                    <td>{{ numberFormatter.formatNumber(rate.rate, 6) }}</td>
                    <td>{{ rate.method }}</td>
                    <td class="text-end">
                        <div v-if="rate.editable">
                          <button type="button" @click.prevent="showEditModal(rate)" class="btn btn-sm btn-outline-dark text-nowrap text-capitalize"><i class="fa fa-pencil"></i></button>
                          <button type="button" @click.prevent="deleteRate(rate)" class="btn btn-sm ms-2 btn-outline-danger"><i class="fa fa-trash"></i></button>

                        </div>
                    </td>
                  </tr>
                  </tbody>
                </table>

                <b-pagination
                  v-if="response.totalCount > response.items.length"
                  v-model="page"
                  :total-rows="response.totalCount"
                  :per-page="response.limit"
                  :limit="20"
                  @update:modelValue="loadList()"
                  :prev-text="helper.trans('button.back')"
                  :next-text="helper.trans('button.next')"
                  align="start"
                  first-number
                  hide-goto-end-buttons
                  hide-ellipsis
                ></b-pagination>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ConfirmModal ref="confirmModal"></ConfirmModal>
    </div>
  </LoaderWrapper>
  </div>
</template>

<script lang="ts">
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import {defineComponent, onMounted, onUnmounted, reactive, ref, toRefs} from 'vue'
import {dateHelper} from "@/service/DateHelper";
import {numberFormatter} from "@/service/NumberFormatter";
import {api} from "@/service/APIClient";
import {useStore} from "vuex";
import {helper} from "@/service/CommonHelper";
import {BPagination} from "bootstrap-vue-3";
import PaginatedResponseDTO from "@/models/PaginatedResponseDTO";
import CurrencyRateDTO from "@/models/CurrencyRateDTO";
import PaginationDTO from "@/models/PaginationDTO";
import CurrencyRateForm from "@/components/Form/CurrencyRateForm.vue";
import ConfirmModal, {ConfirmModalProps} from "@/components/ConfirmModal.vue";

export default defineComponent({
  name: 'CurrencyRates',
  components: {
    ConfirmModal,
    CurrencyRateForm,
    LoaderWrapper,
    BPagination,
  },
  setup() {
    const store = useStore();
    const pageLoader = ref<InstanceType<typeof LoaderWrapper> | null>(null)
    const createForm = ref<InstanceType<typeof CurrencyRateForm> | null>(null)
    const loaderCreateFrom = ref<InstanceType<typeof LoaderWrapper> | null>(null)
    const confirmModal = ref<InstanceType<typeof ConfirmModal> | null>(null)

    const state = reactive({
      page: 1,
      search: '',
      createModal: false,
      createModalTitle: helper.trans("labels.edit_rate"),
      response: {
        limit: 20,
        offset: 0,
        totalCount: 0,
        items: []
      } as PaginatedResponseDTO<CurrencyRateDTO>,
    });

    let requestId = 0;
    let appliedSearch = '';

    const applyFilters = async () => {
      appliedSearch = state.search.trim();
      state.page = 1;
      await loadList();
    }

    const clearFilters = async () => {
      state.search = '';
      await applyFilters();
    }

    onUnmounted(() => {
      requestId++;
    });

    const showEditModal = async (dto: CurrencyRateDTO) => {
      if (dto.id) {
        createForm.value?.setForm({
          ...dto,
        } as CurrencyRateDTO)
      } else {
        createForm.value?.setForm({
          name: "",
          source: "",
          fromCurrency: "",
          toCurrency: "",
          rate: "",
          method: "",
          editable: true
        } as CurrencyRateDTO)
      }

      state.createModal = true;
    }

    const deleteRate = async  (rate: CurrencyRateDTO) => {
      confirmModal.value?.show({
        title: helper.trans('labels.delete_rate_confirmation'),
        okTitle: helper.trans(`button.delete`),
        cancelTitle: null,
        onOkClick: async () => {
          pageLoader.value?.show()
          try {
            await api.currencyRatesDelete(rate.id);
            await loadList()
          } catch (e) {
          } finally {
            pageLoader.value?.hide()
          }
        }
      } as ConfirmModalProps);
    }

    const loadList = async () => {
      const currentRequestId = ++requestId;
      state.response.offset = state.response.limit * (state.page - 1)
      pageLoader.value?.show()
      try {
        const response = await api.currencyRatesOperatorAndAdmin({
          limit: state.response.limit,
          offset: state.response.offset
        } as PaginationDTO, appliedSearch);
        if (currentRequestId !== requestId) {
          return;
        }
        state.response = response;
        const lastPage = Math.max(1, Math.ceil(response.totalCount / response.limit));
        if (state.page > lastPage) {
          state.page = lastPage;
          await loadList();
        }
      } finally {
        if (currentRequestId === requestId) {
          pageLoader.value?.hide()
        }
      }
    }

    const save = () => {
      loaderCreateFrom.value?.show()

      createForm.value!.save()
        .then((rate: CurrencyRateDTO) => {
          state.createModal = false;
          loadList();
        })
        .finally(() => {
          loaderCreateFrom.value?.hide();
        })

    }

    onMounted(async () => {
      await loadList();
    })

    return {
      ...toRefs(state),
      createForm,
      deleteRate,
      save,
      loaderCreateFrom,
      confirmModal,
      showEditModal,
      pageLoader,
      dateHelper,
      numberFormatter,
      helper,
      loadList,
      applyFilters,
      clearFilters,
    }
  }
})
</script> 